import {AttachmentFile} from '@hconnect/common/types'
import {Button, H5, icon, TextRegular, TextSmallRegular} from '@hm/fundamental'
import {useCallback, useEffect, useState} from 'react'
import {useDropzone} from 'react-dropzone'
import {useTranslation} from 'react-i18next'

import {DownloadDrillingResultsExcelTemplate} from '../DownloadDrillingResultsExcelTemplate'

import {
  findDrillingHeaderRowIndex,
  parseDrillingFileData,
  readDrillingFileContent,
  validateDrillingFileData
} from '@refra/modules/refra-planning/helpers'
import type {
  DrillingPosition,
  ParsedDrillingData,
  ShutdownDrillingDTO
} from '@refra/modules/refra-planning/types'

const MAX_DRILLING_RESULTS_FILE_SIZE_MB = 10
const AMOUNT_OF_DRILLING_RESULTS_FILES = 1

const EXCEL_ACCEPT_TYPES = [
  '.xlsx',
  '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
]

interface StepDrillingResultsDropzoneProps {
  cancelUpload: () => void
  onDataImported: (data: ParsedDrillingData) => void
  shutdownId: number
  shutdownName: string
  drillingPositions: DrillingPosition[]
  drillings: ShutdownDrillingDTO[]
  kilnLength: number
}

export const StepDrillingResultsDropzone: React.FC<StepDrillingResultsDropzoneProps> = ({
  cancelUpload,
  onDataImported,
  shutdownId,
  shutdownName,
  drillingPositions,
  drillings,
  kilnLength
}) => {
  const {t} = useTranslation()
  const [files, setFiles] = useState<AttachmentFile[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [parsedData, setParsedData] = useState<ParsedDrillingData | null>(null)

  const handleParseFile = useCallback(
    async (file: File) => {
      setParseError(null)
      setParsedData(null)
      try {
        const rows = await readDrillingFileContent(file)
        const headerRowIndex = findDrillingHeaderRowIndex(rows)

        const enabledPositions = drillingPositions.filter((p) => p.isEnabled)
        const errors = validateDrillingFileData(rows, headerRowIndex, enabledPositions)

        const blockingErrors = errors.filter((e) => e.type === 'missing_header')
        if (blockingErrors.length > 0) {
          setParseError(blockingErrors[0].message)
          return
        }

        const data = parseDrillingFileData(rows, headerRowIndex)
        setParsedData(data)
      } catch (error) {
        setParseError(error instanceof Error ? error.message : 'Failed to parse file')
      }
    },
    [drillingPositions]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        const newFiles: AttachmentFile[] = [
          {
            id: Math.random(),
            name: file.name,
            contentType: file.type,
            preview: URL.createObjectURL(file),
            isImage: file.type.includes('image'),
            originalFile: file
          }
        ]
        setFiles(newFiles)
        void handleParseFile(file)
      }
    },
    [handleParseFile]
  )

  const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
    onDrop,
    accept: EXCEL_ACCEPT_TYPES,
    maxFiles: AMOUNT_OF_DRILLING_RESULTS_FILES,
    maxSize: MAX_DRILLING_RESULTS_FILE_SIZE_MB * 1024 * 1024,
    noClick: true,
    noKeyboard: false
  })

  const handleImport = () => {
    if (parsedData) {
      onDataImported(parsedData)
    }
  }

  const handleRemoveFile = () => {
    // Revoke blob URLs to prevent memory leaks
    files.forEach((file) => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview)
      }
    })
    setFiles([])
    setParsedData(null)
    setParseError(null)
  }

  // Cleanup blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      files.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview)
        }
      })
    }
  }, [files])

  return (
    <div className="flex flex-col gap-4 p-6" data-cy="step_drilling_results_dropzone">
      <div>
        <H5>{t('refraPlanning.drilling.uploadResultsTitle')}</H5>
        <TextRegular style={{color: 'var(--text-secondary)'}}>
          {t('refraPlanning.drilling.uploadResultsDescription')}
        </TextRegular>
      </div>

      <div className="flex flex-col gap-2">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded p-8 text-center cursor-pointer transition-all duration-200 ${
            isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'
          }`}
          data-cy="drilling_results_dropzone"
        >
          <input {...getInputProps()} data-cy="drilling_results_file_input" />
          <div className="flex flex-col gap-2 items-center">
            <Button
              type="button"
              variant="secondary"
              iconPrefix={icon.mdiFileUpload}
              onClick={open}
            >
              {t('common.upload')}
            </Button>
            <TextRegular style={{color: 'var(--text-secondary)'}}>
              {t('refraPlanning.drilling.orDragAndDrop')}
            </TextRegular>
            <TextSmallRegular style={{color: 'var(--text-secondary)'}}>
              {t('refraPlanning.drilling.maxFileSize', {size: MAX_DRILLING_RESULTS_FILE_SIZE_MB})}
            </TextSmallRegular>
          </div>
        </div>

        {files.length > 0 && (
          <div className="mt-4">
            {files.map((file) => (
              <div
                key={file.id}
                className={`flex items-center justify-between p-4 border rounded mb-2 ${
                  parseError ? 'border-red-500 bg-red-50' : 'border-green-500 bg-green-50'
                }`}
                data-cy="attachment_preview"
              >
                <TextRegular>{file.name}</TextRegular>
                <Button
                  type="button"
                  variant="secondary"
                  iconPrefix={icon.mdiClose}
                  onClick={handleRemoveFile}
                  size="small"
                >
                  {t('common.delete')}
                </Button>
              </div>
            ))}
            {parseError && (
              <TextRegular style={{color: 'var(--error)'}} data-cy="parse_error_message">
                {parseError}
              </TextRegular>
            )}
            {parsedData && (
              <TextRegular style={{color: 'var(--success)'}} data-cy="parse_success_message">
                {t('refraPlanning.drilling.fileValidSuccess', {
                  count: parsedData.length,
                  defaultValue: 'File parsed successfully: {{count}} rows found'
                })}
              </TextRegular>
            )}
          </div>
        )}
      </div>

      <DownloadDrillingResultsExcelTemplate
        shutdownId={shutdownId}
        shutdownName={shutdownName}
        drillingPositions={drillingPositions}
        drillings={drillings}
        kilnLength={kilnLength}
      />

      <div className="flex flex-row gap-2 justify-end mt-4">
        <Button
          type="button"
          variant="secondary"
          iconPrefix={icon.mdiClose}
          onClick={cancelUpload}
          data-cy="cancel_upload_button"
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          iconPrefix={icon.mdiCheck}
          onClick={handleImport}
          disabled={!parsedData || !!parseError}
          data-cy="import_drilling_button"
        >
          {t('common.import')}
        </Button>
      </div>
    </div>
  )
}
